import { FC, useContext, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { RoomClass } from 'ts/hotel/interfaces/HotelDetails';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowDownLong,
  faBahtSign,
  faExclamationTriangle,
  faList,
  faSearch,
  faSort,
  faTimes,
  faUtensils,
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { faAccessibleIcon } from '@fortawesome/free-brands-svg-icons';
import { FilterRoom, FilterRoomHotel, getRoomSupplierDesc } from 'ts/hotel/classes/FilterRoom';
import { useSearchParams } from 'react-router-dom';
import Tooltip from 'components/ui/Tooltip/Tooltip';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import { useClickAway } from 'hooks/useClickAway';
import { formatPriceCurrency, getCurrency } from 'utils/utils';
import { GroupedRooms } from 'ts/hotel/interfaces/HotelResponse';
import { getKey, getOriginalHotelKey, HotelDisplaySettings } from 'utils/hotel';
import Dropdown from 'components/shared/Dropdown';
import FontAwesomeLayered from 'components/shared/ui/FontAwesomeLayered';
import { HotelDetailsContext } from 'contexts/HotelDetailsContext';
import { currenciesList, flyeastCompanyId, getPriceModesList } from 'constants/hotel';
import { faArrowDownLeftAndArrowUpRightToCenter } from '@fortawesome/sharp-duotone-solid-svg-icons';
import { MainContext } from 'contexts/MainContext';
import { HotelConfigContext } from 'contexts/HotelConfigContext';
import { b2b } from 'constants/generic';
import {
  clearLocalRoomFilterAction,
  getLocalRoomFilterAction,
  subscribeToLocalRoomFilterAction,
} from 'utils/assistantChatStore';

interface RoomsFilterProps {
  rooms: RoomClass[];
  hotel?: FilterRoomHotel & { htuKey?: string };
  setRooms: (arg: RoomClass[]) => void;
  setFilterRoom?: (arg: FilterRoom) => void;
  grRooms?: GroupedRooms[];
  setGrRooms?: (arg: GroupedRooms[]) => void;
  bigMode?: boolean;
  displaySettings?: HotelDisplaySettings;
  onChangeDisplaySettings?: (settings: HotelDisplaySettings) => void;
  showAllRooms?: boolean;
  onShowAllRoomsChange?: (showAll: boolean) => void;
  totalRoomsCount?: number;
}

const RoomsFilter: FC<RoomsFilterProps> = ({ rooms, setRooms, hotel, ...props }) => {
  const [t] = useTranslation();
  const { hotelIndex } = useContext(HotelConfigContext);
  const { user, initSiteData } = useContext(MainContext);
  const [filterRoom, setFilterRoom] = useState(new FilterRoom(rooms, hotel, hotelIndex));
  const [urlParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [currencyState, setCurrencyState] = useState(currenciesList);
  const currency = sessionStorage.getItem(getKey('displayCurrencyHotels', hotelIndex)) || 'usd';
  const firstLoad = useRef(true);
  const sort = useRef(0);
  const suppRef = useClickAway(() => {
    setOpen(false);
  });
  const { roomTypes } = useContext(HotelDetailsContext);
  const localRoomFilter = useSyncExternalStore(
    subscribeToLocalRoomFilterAction,
    getLocalRoomFilterAction,
  );

  useEffect(() => {
    if (initSiteData?.companyId === flyeastCompanyId && urlParams.get('id') === 'TH') {
      setCurrencyState(
        currenciesList.concat({
          value: 'thb',
          label: '',
          icon: {
            path: faBahtSign,
            className: '',
          },
        })
      );
    }
  }, [urlParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    filterRooms(filterRoom);
  }, [roomTypes]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!localRoomFilter || !hotel?.htuKey) {
      return;
    }
    if (localRoomFilter.hotelKey !== getOriginalHotelKey(hotel.htuKey)) {
      return;
    }

    let next = localRoomFilter.clearRoomFilters
      ? new FilterRoom(rooms, hotel, hotelIndex)
      : { ...filterRoom };

    if (typeof localRoomFilter.roomFilterName === 'string') {
      next = { ...next, name: localRoomFilter.roomFilterName };
    }
    if (typeof localRoomFilter.roomRefundableOnly === 'boolean') {
      next = { ...next, refund: localRoomFilter.roomRefundableOnly };
    }
    if (typeof localRoomFilter.roomAccessibleOnly === 'boolean') {
      next = { ...next, accessible: localRoomFilter.roomAccessibleOnly };
    }
    if (typeof localRoomFilter.roomBoardingOnly === 'boolean') {
      next = { ...next, boarding: localRoomFilter.roomBoardingOnly };
    }
    if (Array.isArray(localRoomFilter.roomMealPlans)) {
      next = {
        ...next,
        meal: {
          ...next.meal,
          value: localRoomFilter.roomMealPlans.filter((code) => next.meal.active.includes(code)),
        },
      };
    }

    filterRooms(next);

    if (typeof localRoomFilter.roomSortByCxl === 'boolean') {
      onClickSort(localRoomFilter.roomSortByCxl ? 6 : 1);
    }

    clearLocalRoomFilterAction();
  }, [localRoomFilter?.version]); // eslint-disable-line

  useEffect(() => {
    if (firstLoad.current) {
      const newFilter = new FilterRoom(rooms, hotel, hotelIndex);
      let { filterBoarding, filterAccessible, filterRefund, filterMeal, details } =
        Object.fromEntries(urlParams);

      if (filterBoarding === undefined) {
        const detailsJson = new URLSearchParams(details);
        filterBoarding = detailsJson.get('filterBoarding') || '0';
        filterAccessible = detailsJson.get('filterAccessible') || '0';
        filterRefund = detailsJson.get('filterRefund') || '0';
        filterMeal = detailsJson.get('filterMeal') || '';
      }

      let urlValue = filterMeal?.split(',') || [];
      urlValue = urlValue.filter((val) => newFilter.meal.active.includes(val));

      const urlFilter = {
        ...newFilter,
        boarding: filterBoarding === '1',
        accessible: filterAccessible === '1',
        refund: filterRefund === '1',
        meal: {
          ...newFilter.meal,
          value: urlValue,
        },
      };

      filterRooms(urlFilter);
      firstLoad.current = false;
    } else {
      const newFilter = new FilterRoom(rooms, hotel, hotelIndex);

      const newSupppliers = newFilter.suppliers?.map((sup) => {
        const prev = filterRoom.suppliers?.find((s) => s.desc === sup.desc);
        return { ...sup, checked: prev?.checked || false };
      });

      filterRooms({
        ...filterRoom,
        suppliers: newSupppliers,
        meal: {
          ...filterRoom.meal,
          active: newFilter.meal.active,
        },
      });
    }

    if (sort.current) {
      onClickSort(sort.current);
    }
  }, [rooms.length]); //eslint-disable-line

  const filterRooms = (filter: FilterRoom) => {
    setFilterRoom(filter);
    props.setFilterRoom?.(filter);

    const newRooms = rooms.map((room) => {
      const visibleName = filter.name
        .split(' ')
        .every((name) =>
          room.hotelRooms[0].displayName?.toLowerCase().includes(name?.toLowerCase())
        );

      const visibleBoarding =
        !filter.boarding || (filter.boarding && room.board?.basis?.code !== 'RO');

      const visibleAccessible =
        !filter.accessible || (filter.accessible && !!room.hotelRooms?.[0].ada);

      const visibleRefund =
        !filter.refund || (filter.refund && (new Date(room.cxl.limitDate) > new Date() || new Date(room.cxl.limitDate) < new Date(2000, 1, 1)));

      const visibleSupp =
        !filter.suppliers?.some((sup) => sup.checked) ||
        filter.suppliers?.some(
          (sup) => sup.checked && sup.desc === getRoomSupplierDesc(room, hotel),
        );
      const visibleMeal =
        !filter.meal.value.length ||
        filter.meal.value?.some((meal) => meal === room.board?.basis?.code);

      const visibleRoomTypes =
        !roomTypes.some((type) => type.options.some((opt) => opt.checked)) ||
        roomTypes.every((type) => {
          const checkedOptions = type.options.filter((opt) => opt.checked);
          if (checkedOptions.length === 0) return true;

          const roomValue = (room.hotelRooms[0] as any)[type.title];
          if (!roomValue) return false;

          const roomValues = roomValue.split(', ').map((v: string) => v.trim());
          return checkedOptions.some((opt) => roomValues.includes(opt.name));
        });

      return {
        ...room,
        visibleSm:
          visibleName &&
          visibleBoarding &&
          visibleAccessible &&
          visibleRefund &&
          visibleSupp &&
          visibleMeal &&
          visibleRoomTypes,
      };
    });
    setRooms(newRooms);

    if (props.grRooms && props.setGrRooms) {
      const newGroupedRooms = props.grRooms.map((groupedRoom) => {
        const newGroupedRoom = {
          ...groupedRoom,
          data: groupedRoom.data.map((room) => {
            const filteredByName = filter.name
              .split(' ')
              .every((name) =>
                room.hotelRooms[0].displayName.toLowerCase().includes(name?.toLowerCase())
              );
            const filteredByBoarding = !filter.boarding || room.board?.basis?.code !== 'RO';
            const filteredByCxl = !filter.refund || new Date(room.cxl.limitDate) > new Date();
            const filteredByAccess = !filter.accessible || room.hotelRooms[0].ada;

            const visibleSupp =
              !filter.suppliers?.some((sup) => sup.checked) ||
              filter.suppliers?.some(
                (sup) => sup.checked && sup.desc === getRoomSupplierDesc(room, hotel),
              );
            const visibleMeal =
              !filter.meal.value.length ||
              filter.meal.value?.some((meal) => meal === room.board?.basis?.code);

            const visibleRoomTypes =
              !roomTypes.some((type) => type.options.some((opt) => opt.checked)) ||
              roomTypes.every((type) => {
                const checkedOptions = type.options.filter((opt) => opt.checked);
                if (checkedOptions.length === 0) return true;

                const roomValue = (room.hotelRooms[0] as any)[type.title];
                if (!roomValue) return false;

                const roomValues = roomValue.split(', ').map((v: string) => v.trim());
                return checkedOptions.some((opt) => roomValues.includes(opt.name));
              });

            return {
              ...room,
              visibleSm:
                filteredByName &&
                filteredByBoarding &&
                filteredByCxl &&
                filteredByAccess &&
                visibleMeal &&
                visibleSupp &&
                visibleRoomTypes,
            };
          }),
        };

        return newGroupedRoom;
      });
      props.setGrRooms(newGroupedRooms);
    }
  };

  const onChangeSupp = (val: boolean, index: number) => {
    let newSupp = [...filterRoom.suppliers!];
    newSupp[index].checked = val;
    const newFilter = { ...filterRoom, suppliers: newSupp };
    filterRooms(newFilter);
  };

  const onClickMeal = (option: string) => {
    if (filterRoom.meal.active.includes(option)) {
      const newFilter = {
        ...filterRoom,
        meal: {
          ...filterRoom.meal,
          value: filterRoom.meal.value.includes(option)
            ? filterRoom.meal.value.filter((v) => v !== option)
            : [...filterRoom.meal.value, option],
        },
      };

      filterRooms(newFilter);
    }
  };
  const onClickSort = (sortNumber: number) => {
    const newRooms = [...rooms];
    sort.current = sortNumber;

    if (sortNumber === 1) {
      newRooms.sort((a, b) => a.price.value - b.price.value);
    } else if (sortNumber === 2) {
      newRooms.sort((a, b) => b.price.value - a.price.value);
    } else if (sortNumber === 3) {
      newRooms.sort((a, b) =>
        a.hotelRooms[0].displayName.localeCompare(b.hotelRooms[0].displayName)
      );
    } else if (sortNumber === 4) {
      newRooms.sort((a, b) =>
        b.hotelRooms[0].displayName.localeCompare(a.hotelRooms[0].displayName)
      );
    } else if (sortNumber === 5) {
      newRooms.sort(
        (a, b) => new Date(a.cxl.limitDate).getTime() - new Date(b.cxl.limitDate).getTime()
      );
    } else if (sortNumber === 6) {
      newRooms.sort(
        (a, b) => new Date(b.cxl.limitDate).getTime() - new Date(a.cxl.limitDate).getTime()
      );
    }

    setRooms(newRooms);
  };

  return (
    <div className={`hotel-card-room room-header ${props.bigMode ? 'room-header-big' : ''}`}>
      <div className="room-name flex-row">
        <div className="room-sort">
          <Dropdown
            byClick={true}
            opener={
              <Tooltip content={t('sort.by')} position="top">
                <FontAwesomeIcon
                  icon={faSort}
                  color={sort.current ? 'var(--color-contrast)' : '#333'}
                />
              </Tooltip>
            }
          >
            <div className="c-dropdown-row" onClick={() => onClickSort(1)}>
              {t('sort.price_increase')}{' '}
            </div>
            <div className="c-dropdown-row" onClick={() => onClickSort(2)}>
              {t('sort.price_decrease')}
            </div>
            <div className="c-dropdown-row" onClick={() => onClickSort(3)}>
              {t('sort.hotel_increase')}
            </div>
            <div className="c-dropdown-row" onClick={() => onClickSort(4)}>
              {t('sort.hotel_decrease')}
            </div>
            <div className="c-dropdown-row" onClick={() => onClickSort(5)}>
              {t('sort.cxl_increase')}
            </div>
            <div className="c-dropdown-row" onClick={() => onClickSort(6)}>
              {t('sort.cxl_decrease')}
            </div>
          </Dropdown>
        </div>
        <div className="mr-5"></div>{' '}
        <span className="room-search-icon">
          <FontAwesomeIcon icon={faSearch} />
        </span>
        <input
          type="text"
          className="form-control"
          value={filterRoom.name}
          onChange={(e) => filterRooms({ ...filterRoom, name: e.target.value })}
        />
        {user?.branch?.hotelDisplaySupplierName && (
          <div className="position-relative ml-5">
            <Tooltip content={t('hotel.supplier')} position="top">
              <div className="form-control cur-poi form-control-sm" onClick={() => setOpen(!open)}>
                <FontAwesomeIcon icon={faList} />
              </div>
            </Tooltip>

            {open && (
              <div className="dp-wrapper dp2-wrapper" ref={suppRef}>
                {!!filterRoom?.suppliers?.length && (
                  <table className="hotel-supplier-table">
                    <thead>
                      <tr>
                        <th>{t('hotel.supplier')}</th>
                        <th>{t('hotel.rooms')}</th>
                        <th>{t('price')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filterRoom.suppliers.map((sup, index) => (
                        <tr key={index} className="text-normal">
                          <td>
                            <Checkbox
                              checked={sup.checked}
                              label={sup.desc}
                              onChange={(val: boolean) => onChangeSupp(val, index)}
                            />
                          </td>

                          <td className="mr-5">{sup.count}</td>
                          <td>({formatPriceCurrency(sup.minPrice, getCurrency(currency))})</td>
                        </tr>
                      ))}

                      <tr className="text-bold ">
                        <td>{t('total')}:</td>
                        <td>{filterRoom.suppliers?.reduce((acc, sup) => acc + sup.count, 0)}</td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        )}

        <div className="room-icon active-i ml-5 room-icon-cxlsort">
          <Checkbox
            checked={sort.current === 6}
            onChange={(checked) => onClickSort(checked ? 6 : 1)}
            htmlLabel={
              <span>
                <FontAwesomeLayered
                  icon1={faArrowDownLong}
                  icon2={faArrowDownLeftAndArrowUpRightToCenter}
                  className="color-danger"
                />
                <span className={'text-checkbox ' + (sort.current === 6 ? '' : 'text-normal')}>
                  CXL
                </span>
              </span>
            }
          />
        </div>
        {props.onShowAllRoomsChange && (
          <div className="room-icon active-i ml-5">
            <Checkbox
              checked={!!props.showAllRooms}
              onChange={props.onShowAllRoomsChange}
              htmlLabel={
                <span className={'text-checkbox ' + (props.showAllRooms ? '' : 'text-normal')}>
                  {t('hotel.show_all_rooms')} ({props.totalRoomsCount ?? rooms.length})
                </span>
              }
            />
          </div>
        )}
      </div>
      <div className="mr-5">
        {!!(
          filterRoom.name ||
          filterRoom.boarding ||
          filterRoom.refund ||
          filterRoom.accessible ||
          filterRoom.meal.value.length ||
          filterRoom.suppliers?.some((s) => s.checked)
        ) && (
            <span
              className="text-xsm text-normal text-underline color-danger"
              onClick={() => filterRooms(new FilterRoom(rooms, hotel, hotelIndex))}
            >
              <Tooltip content={t('filter.reset')} position="top">
                <FontAwesomeIcon icon={faTimes} size="lg" />
              </Tooltip>
            </span>
          )}
      </div>

      {props.displaySettings && props.onChangeDisplaySettings && b2b && !!user?.showNet && (
        <div
          className={`hotel-result-filter-input position-relative${props.bigMode ? ' h44p' : ''}`}
          style={{ maxWidth: 75 }}
        >
          <SelectCustom
            options={getPriceModesList(user)}
            value={props.displaySettings.priceMode}
            onChange={(value) =>
              props.onChangeDisplaySettings!({ ...props.displaySettings!, priceMode: value })
            }
            isDropdown
          />
        </div>
      )}
      {props.displaySettings && props.onChangeDisplaySettings && b2b && (
        <Tooltip content={t('mo.currency')} position="top">
          <div className={`hotel-result-filter-input supp position-relative${props.bigMode ? ' h44p' : ''}`}>
            <SelectCustom
              options={currencyState}
              value={props.displaySettings.currency}
              onChange={(value) =>
                props.onChangeDisplaySettings!({ ...props.displaySettings!, currency: value })
              }
              showOnlyIcon
              isDropdown
            />
          </div>
        </Tooltip>
      )}
      <div className={'room-icon active-i' + (filterRoom.accessible ? ' active-ada-i' : '')}>
        <Checkbox
          checked={filterRoom.accessible}
          onChange={(accessible) => filterRooms({ ...filterRoom, accessible })}
          htmlLabel={<FontAwesomeIcon icon={faAccessibleIcon} />}
        />
      </div>
      <div className={'room-icon active-i' + (filterRoom.refund ? ' active-refund-i' : '')}>
        <Checkbox
          checked={filterRoom.refund}
          onChange={(refund) => filterRooms({ ...filterRoom, refund })}
          htmlLabel={
            <span>
              <FontAwesomeIcon icon={faExclamationTriangle} className="mr-3" />
              <span className={'text-checkbox ' + (filterRoom.refund ? '' : 'text-normal')}>
                {t('filter.free_x')}
              </span>
            </span>
          }
        />
      </div>

      {!b2b && (
        <div className={'room-icon active-i' + (filterRoom.boarding ? ' active-meal-i' : '')}>
          <Checkbox
            checked={filterRoom.boarding}
            onChange={(boarding) => filterRooms({ ...filterRoom, boarding })}
            htmlLabel={<FontAwesomeIcon icon={faUtensils} />}
          />
        </div>
      )}

      {b2b && (
        <div className="hotel-result-filter-input star">
          <div className="star-wr text-normal" style={{ height: 27, borderRadius: 4 }}>
            {filterRoom.meal.options?.map((option, index) => (
              <Tooltip
                content={t(filterRoom.meal.tooltips?.[index] || '')}
                key={index}
                position="top"
              >
                <div
                  className={
                    'cur-poi flex-center h100p w100p' +
                    (filterRoom.meal.value?.includes(option) ? ' active' : '') +
                    (!filterRoom.meal.active.includes(option) ? ' disabled' : '')
                  }
                  key={index}
                  onClick={() => onClickMeal(option)}
                >
                  <span>{option}</span>
                </div>
              </Tooltip>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomsFilter;
